/**
 * Import orchestration: files (PNG / JSON), share links and legacy v2 Imgur
 * links all end up here. Browser specifics (fetch, image decoding) are
 * injected so this runs in Node tests.
 */
import type { KinklistDoc } from '../model/types';
import { utf8Decode } from './bytes';
import { ImportError } from './errors';
import { docFromJson } from './payload';
import type { RgbaImage } from './pixel-codec';
import { decodePixelStrip, decodeStripText } from './pixel-codec';
import { isPng, readChunks, readITXt } from './png-chunks';
import { UnsupportedPngError, decodePng } from './png-decode';
import { decodeLink } from './link';
import type { HydrateContext } from './wire';

export const ITXT_KEYWORD = 'kinklist';
export const MAX_PNG_BYTES = 20 * 1024 * 1024;
export const MAX_JSON_BYTES = 1024 * 1024;

export interface ImportDeps extends HydrateContext {
  /** Browser fallback for PNGs the pure decoder doesn't support. */
  decodeImage?: (bytes: Uint8Array<ArrayBuffer>) => Promise<RgbaImage>;
  fetch?: (url: string) => Promise<Response>;
}

/** Anything File-like. */
export interface ImportFile {
  name: string;
  size: number;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export async function importPng(
  bytes: Uint8Array<ArrayBuffer>,
  deps: ImportDeps,
): Promise<KinklistDoc> {
  const chunks = readChunks(bytes);
  const embedded = readITXt(chunks, ITXT_KEYWORD);
  if (embedded !== null) {
    try {
      return docFromJson(embedded, deps);
    } catch {
      // Damaged chunk: fall back to the pixel strip.
    }
  }

  let image: RgbaImage;
  try {
    image = decodePng(bytes);
  } catch (error) {
    if (!(error instanceof UnsupportedPngError) || !deps.decodeImage) throw error;
    image = await deps.decodeImage(bytes);
  }
  const strip = decodePixelStrip(image);
  if (strip === null) throw new ImportError('no-data', 'This image has no embedded Kinklist data');
  return docFromJson(decodeStripText(strip), deps);
}

export async function importFile(file: ImportFile, deps: ImportDeps): Promise<KinklistDoc> {
  if (file.size > MAX_PNG_BYTES) throw new ImportError('too-large', 'File is too large');
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (isPng(bytes)) return importPng(bytes, deps);
  const looksLikeJson = /\.json$/i.test(file.name) || bytes.find((b) => b > 0x20) === 0x7b;
  if (!looksLikeJson) throw new ImportError('unsupported-file', 'Expected a PNG or JSON file');
  if (bytes.length > MAX_JSON_BYTES) throw new ImportError('too-large', 'File is too large');
  return docFromJson(utf8Decode(bytes), deps);
}

export function importShare(payload: string, deps: ImportDeps): KinklistDoc {
  return decodeLink(payload, deps);
}

/**
 * Legacy v2 view link (`#<imgurId>`): the only network request the app ever
 * makes, and only when such a link is opened.
 */
export async function importImgur(id: string, deps: ImportDeps): Promise<KinklistDoc> {
  const doFetch = deps.fetch ?? ((url: string) => globalThis.fetch(url));
  let response: Response;
  try {
    response = await doFetch(`https://i.imgur.com/${encodeURIComponent(id)}.png`);
  } catch (cause) {
    throw new ImportError('network', 'Could not reach Imgur', { cause });
  }
  if (response.status === 404) throw new ImportError('not-found', 'The image no longer exists');
  if (!response.ok) throw new ImportError('network', `Imgur responded ${String(response.status)}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.length > MAX_PNG_BYTES) throw new ImportError('too-large', 'Image is too large');
  // Removed images redirect to a placeholder (a real PNG without data → `no-data`).
  if (!isPng(bytes)) throw new ImportError('not-found', 'Not a PNG image');
  return importPng(bytes, deps);
}
